import { withInternshipSeo } from './internshipSeo';

export const DEFAULT_INTERNSHIP_PLANS = [
  { id: 1, name: '1 Month', months: 1, weeks: 4, price: 300, description: 'Starter track — weekly tasks and offer letter.' },
  { id: 2, name: '2 Months', months: 2, weeks: 8, price: 500, description: 'Deeper build with two sprint cycles.' },
  { id: 3, name: '6 Months', months: 6, weeks: 26, price: 1000, description: 'Long-form internship with rotating weekly briefs.' },
  { id: 4, name: '1 Year', months: 12, weeks: 52, price: 2000, description: 'Full-year programme with completion letter after review.' }
];

const COVERS = {
  'full-stack-developer': 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=1400&q=70',
  'frontend-developer': 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1400&q=70',
  'backend-developer': 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1400&q=70',
  'website-developer': 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=1400&q=70',
  'software-developer': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1400&q=70',
  'java-developer': 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1400&q=70',
  'python-developer': 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1400&q=70',
  'flutter-app-developer': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1400&q=70',
  'android-app-developer': 'https://images.unsplash.com/photo-1607252650355-f7fd0460ccdb?auto=format&fit=crop&w=1400&q=70',
  'ai-machine-learning': 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1400&q=70',
  'ai-automation': 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=1400&q=70',
  'ui-ux-designer': 'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=1400&q=70',
  'graphic-designer': 'https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1400&q=70',
  'digital-marketing': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=70',
  'cyber-security': 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1400&q=70'
};

export const internshipCover = (slug) =>
  COVERS[slug] || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=70';

export const internships = [
  {
    slug: 'full-stack-developer',
    title: 'Full Stack Developer Intern',
    category: 'engineering',
    keywords: 'full stack internship Satara, MERN intern, React Node.js internship India',
    summary: 'Build complete web products with React, APIs and databases on live Growmore projects.',
    about: 'You practise full-stack in small weekly pieces: one screen, one API, one save. Friendly briefs, not a giant project.',
    learn: ['React + Tailwind screens', 'Laravel / Node APIs', 'MySQL / SQLite models', 'Git, testing and go-live'],
    problems: [
      'Save one simple form',
      'Make a small list with add and edit',
      'Connect a login box to an API'
    ],
    features: [
      'Auth + protected pages',
      'REST endpoints with validation',
      'Database tables and simple reports',
      'Deploy-ready folder and README'
    ],
    eligibility: ['Basic HTML, CSS and JavaScript', 'College student or recent graduate', 'Laptop with internet'],
    tools: ['VS Code', 'GitHub', 'Postman', 'Chrome DevTools']
  },
  {
    slug: 'frontend-developer',
    title: 'Frontend Developer Intern',
    category: 'engineering',
    keywords: 'frontend internship Satara, React intern, UI developer internship',
    summary: 'Create fast, accessible interfaces with React, Tailwind and real client layouts.',
    about: 'You practise UI in small weekly pieces: one layout, one form, one reusable block. Anyone can start — tasks stay short.',
    learn: ['React components', 'Responsive Tailwind UI', 'Accessibility basics', 'Performance and SEO markup'],
    problems: [
      'Make one page look good on a phone',
      'Fix labels and a submit button on a short form',
      'Reuse one card on two screens'
    ],
    features: [
      'Pixel-clean responsive pages',
      'Reusable cards, nav and form blocks',
      'Accessible contrast, focus and alt text',
      'SEO-ready headings and meta-ready markup'
    ],
    eligibility: ['HTML/CSS/JS basics', 'Eye for layout', 'Open to design feedback'],
    tools: ['Figma inspect', 'React', 'Tailwind CSS']
  },
  {
    slug: 'backend-developer',
    title: 'Backend Developer Intern',
    category: 'engineering',
    keywords: 'backend internship Satara, API intern, Laravel PHP internship',
    summary: 'Design APIs, databases and auth flows that power websites and internal tools.',
    about: 'You practise APIs in small weekly pieces: one endpoint, one table, one check. Short briefs you can finish in the week.',
    learn: ['REST APIs', 'Database design', 'Validation and security', 'File storage and admin APIs'],
    problems: [
      'Accept a simple enquiry on an API',
      'Store one list in a table',
      'Upload and list a small file'
    ],
    features: [
      'Validated REST endpoints',
      'Auth and role-aware routes',
      'Clean migrations and seed data',
      'Postman collection for the team'
    ],
    eligibility: ['Any programming language basics', 'SQL curiosity', 'Logical problem solving'],
    tools: ['Laravel', 'Postman', 'SQLite / MySQL']
  },
  {
    slug: 'website-developer',
    title: 'Website Developer Intern',
    category: 'engineering',
    keywords: 'website development internship Satara, WordPress intern, business website intern',
    summary: 'Build SEO-ready business websites for local and national clients.',
    about: 'You build small website pieces each week: a page, a form, a mobile layout. Simple work a local business can use.',
    learn: ['Semantic HTML', 'Responsive pages', 'On-page SEO', 'Contact and enquiry flows'],
    problems: [
      'Add a clear call-to-action on one page',
      'Make a page comfortable on a phone',
      'Connect a short contact form'
    ],
    features: [
      'Home, services, about and contact structure',
      'On-page titles, headings and alt text',
      'Mobile-first layout',
      'Working enquiry / WhatsApp lead path'
    ],
    eligibility: ['HTML/CSS basics', 'Interest in web design', 'Good communication'],
    tools: ['HTML/CSS', 'React', 'Page speed tools']
  },
  {
    slug: 'software-developer',
    title: 'Software Developer Intern',
    category: 'engineering',
    keywords: 'software developer internship Satara, custom software intern, ERP intern',
    summary: 'Work on custom software modules, workflows and admin dashboards.',
    about: 'You turn one small process into screens each week — add, edit, list. Short modules, not a full ERP.',
    learn: ['Requirement to feature mapping', 'CRUD modules', 'Role-based access', 'Simple reporting'],
    problems: [
      'Replace one paper list with a simple screen',
      'Show a different button for admin vs user',
      'Export a short table'
    ],
    features: [
      'CRUD for one real workflow',
      'Status fields and filters',
      'Basic role-based access',
      'A one-page usage note for the client'
    ],
    eligibility: ['Programming fundamentals', 'Patience with details', 'College ID'],
    tools: ['Git', 'SQL', 'React + Laravel']
  },
  {
    slug: 'java-developer',
    title: 'Java Developer Intern',
    category: 'engineering',
    keywords: 'Java internship Satara, core Java intern, OOP internship',
    summary: 'Strengthen core Java, OOP and backend problem solving with weekly coding tasks.',
    about: 'You practise Java in small weekly pieces: one class, one list, one tiny program. Short tasks you can screenshot and send.',
    learn: ['OOP and collections', 'Exception handling', 'Simple REST with Java', 'Unit-style checks'],
    problems: [
      'Write one small class that is easy to extend',
      'Show a clear error when input is empty',
      'Use a list or map for a short demo'
    ],
    features: [
      'Clean OOP structure',
      'Exception-safe console or API demo',
      'Simple REST endpoint in Java',
      'Short write-up of time and space choices'
    ],
    eligibility: ['Core Java basics', 'IDE setup (IntelliJ / Eclipse)', 'Logic building'],
    tools: ['Java 17+', 'IntelliJ / Eclipse', 'Git']
  },
  {
    slug: 'python-developer',
    title: 'Python Developer Intern',
    category: 'engineering',
    keywords: 'Python internship Satara, Python developer intern, scripting internship',
    summary: 'Write Python scripts, APIs and automation that save hours of manual work.',
    about: 'You write small Python scripts each week — one file, one report, one tidy-up. Anyone can start.',
    learn: ['Python scripting', 'APIs and JSON', 'Data cleaning', 'Simple dashboards / reports'],
    problems: [
      'Clean a short spreadsheet',
      'Read a small JSON file and print a summary',
      'Rename or list files in one folder'
    ],
    features: [
      'Reusable Python scripts',
      'API fetch and JSON handling',
      'CSV / Excel clean-up',
      'A short run-book for the next intern'
    ],
    eligibility: ['Python fundamentals', 'Interest in automation', 'College student'],
    tools: ['Python 3', 'VS Code', 'Jupyter optional']
  },
  {
    slug: 'flutter-app-developer',
    title: 'Flutter App Developer Intern',
    category: 'engineering',
    keywords: 'Flutter internship Satara, Dart intern, cross platform app internship',
    summary: 'Build Android/iOS UI with Flutter and Dart on a guided product brief.',
    about: 'You build small Flutter screens each week: one list, one form, one navigation step. Short sprints you can run on a phone.',
    learn: ['Flutter widgets', 'State and navigation', 'API lists', 'Play-store ready UI hygiene'],
    problems: [
      'Open a second screen and come back',
      'Show a short list with an empty state',
      'Check a form before submit'
    ],
    features: [
      'Navigation + named routes',
      'List, detail and form screens',
      'API-backed cards',
      'Clean spacing, fonts and APK share for review'
    ],
    eligibility: ['Any programming basics', 'Android Studio / VS Code', 'Android phone for testing'],
    tools: ['Flutter', 'Dart', 'Android Studio']
  },
  {
    slug: 'android-app-developer',
    title: 'Android App Developer Intern',
    category: 'engineering',
    keywords: 'Android internship Satara, Kotlin intern, mobile app internship',
    summary: 'Create native Android screens, forms and local data flows.',
    about: 'You practise native Android in small weekly pieces: one screen, one list, one save. Easy to install and screenshot.',
    learn: ['Kotlin / Java Android', 'Layouts and lists', 'Intents and forms', 'APK sharing for review'],
    problems: [
      'Open home after a simple login',
      'Show a short list on screen',
      'Save one value when the app closes'
    ],
    features: [
      'Login → home flow',
      'List and detail screens',
      'Local save (SharedPreferences / simple DB)',
      'Signed debug APK for the reviewer'
    ],
    eligibility: ['Java or Kotlin basics', 'Android Studio', 'Emulator or device'],
    tools: ['Android Studio', 'Kotlin', 'Git']
  },
  {
    slug: 'ai-machine-learning',
    title: 'AI & Machine Learning Intern',
    category: 'ai',
    keywords: 'AI internship Satara, machine learning intern, ML internship India',
    summary: 'Learn practical ML: datasets, simple models and clear result reports.',
    about: 'You try small ML steps each week: clean a sheet, train a tiny model, write three lines of findings. Simple and guided.',
    learn: ['Dataset cleaning', 'Train/test split', 'Simple classifiers', 'Charts and written findings'],
    problems: [
      'Tidy a small CSV',
      'Train one simple model',
      'Write a short result in plain language'
    ],
    features: [
      'Cleaned dataset + notebook',
      'Train/test split and a baseline model',
      'Accuracy / confusion notes',
      'A one-page findings report'
    ],
    eligibility: ['Python basics', 'School/college maths comfort', 'Curiosity about data'],
    tools: ['Python', 'Pandas', 'scikit-learn', 'Matplotlib']
  },
  {
    slug: 'ai-automation',
    title: 'AI Automation Intern',
    category: 'ai',
    keywords: 'AI automation internship Satara, workflow automation intern, chatbot intern',
    summary: 'Design rule-based automations, WhatsApp-style flows and SOP bots for real operations.',
    about: 'You map one small process each week: replies, a checklist, a follow-up. Short, practical tasks.',
    learn: ['Process mapping', 'Rule-based bots', 'Lead follow-up flows', 'SOP documentation'],
    problems: [
      'Write three ready WhatsApp replies',
      'Draw a simple follow-up flow',
      'Turn one office step into a short SOP'
    ],
    features: [
      'Process map of a real workflow',
      'Keyword / rule-based reply set',
      'Follow-up sequence (day 0–3)',
      'SOP the next intern can run'
    ],
    eligibility: ['Clear written English/Marathi', 'Logical thinking', 'Interest in operations'],
    tools: ['Flowcharts', 'Spreadsheets', 'No-code tools']
  },
  {
    slug: 'ui-ux-designer',
    title: 'UI/UX Designer Intern',
    category: 'design',
    keywords: 'UI UX internship Satara, Figma intern, product design internship',
    summary: 'Design wireframes, high-fidelity screens and a short case study for a real product brief.',
    about: 'You design small Figma pieces each week: a flow, a few screens, a short note. Anyone can start.',
    learn: ['User flows', 'Wireframes', 'Hi-fi UI', 'Design handoff notes'],
    problems: [
      'Draw a simple first-time user path',
      'Make one screen fit a phone',
      'Write spacing notes for a developer'
    ],
    features: [
      'User flow + wireframes',
      'Hi-fi mobile and desktop frames',
      'Empty, loading and error states',
      'Handoff notes (type, colour, components)'
    ],
    eligibility: ['Figma or similar basics', 'Visual sense', 'Openness to critique'],
    tools: ['Figma', 'FigJam', 'Unsplash']
  },
  {
    slug: 'graphic-designer',
    title: 'Graphic Designer Intern',
    category: 'design',
    keywords: 'graphic design internship Satara, Canva intern, social media design intern',
    summary: 'Create brand posts, banners and a mini identity kit for Growmore-style campaigns.',
    about: 'You make small creatives each week: a post set, a banner, matching colours. Short, visual tasks.',
    learn: ['Brand consistency', 'Social post sets', 'Print vs digital', 'Export for web'],
    problems: [
      'Match colour and type on two posts',
      'Make a banner readable on a phone',
      'Export the right size for Instagram'
    ],
    features: [
      'A 5-post social set',
      'Logo lockup + colour + type note',
      'Story and feed sizes',
      'Print-safe and web-safe exports'
    ],
    eligibility: ['Canva / Photoshop / Illustrator', 'Creativity', 'Attention to alignment'],
    tools: ['Canva or Adobe', 'Google Fonts']
  },
  {
    slug: 'digital-marketing',
    title: 'Digital Marketing Intern',
    category: 'marketing',
    keywords: 'digital marketing internship Satara, SEO intern, social media marketing intern',
    summary: 'Plan SEO, content and campaign calendars for a local IT brand.',
    about: 'You plan small marketing pieces each week: keywords, one post plan, one metric. Simple and guided.',
    learn: ['Keyword research', 'On-page SEO notes', 'Content calendar', 'Basic analytics plan'],
    problems: [
      'List keywords for three services',
      'Plan one week of posts',
      'Pick one number to measure'
    ],
    features: [
      'Keyword list for 5 services',
      'On-page SEO checklist',
      '4-week content calendar',
      'A one-page “what we will measure” sheet'
    ],
    eligibility: ['Good writing', 'Social media familiarity', 'College student'],
    tools: ['Google Keyword ideas', 'Canva', 'Sheets']
  },
  {
    slug: 'cyber-security',
    title: 'Cyber Security Intern',
    category: 'security',
    keywords: 'Cyber Security Internship in Satara, VAPT internship Satara, ethical hacking intern, website security intern India',
    summary: 'Learn defensive security: checklists, password hygiene, basic recon notes and a risk report.',
    about: 'You practise defensive security in small weekly notes: a checklist, a password tip, a short risk line. Simple and legal.',
    learn: ['Security checklist', 'OWASP awareness', 'Password & access hygiene', 'Incident notes'],
    problems: [
      'Fill a short go-live checklist',
      'Write three password tips',
      'Note one risk in plain language'
    ],
    features: [
      'Go-live security checklist',
      'OWASP-style notes on a sample app',
      'Access and password hygiene guide',
      'A short risk report with severity and fix'
    ],
    eligibility: ['Networking curiosity', 'Ethics first', 'Linux interest is a plus'],
    tools: ['Browser tools', 'Notes', 'Optional Kali concepts']
  }
];

export const internshipBySlug = (slug) => withInternshipSeo(internships.find((item) => item.slug === slug));

export const internshipCategories = [
  { id: 'all', name: 'All roles' },
  { id: 'engineering', name: 'Development' },
  { id: 'ai', name: 'AI' },
  { id: 'design', name: 'Design' },
  { id: 'marketing', name: 'Marketing' },
  { id: 'security', name: 'Security' }
];

export const internshipCategoryLabel = (id) =>
  internshipCategories.find((item) => item.id === id)?.name || id;
